import type { Board } from "../board";
import { DamageType } from "../damageType";
import { PRIORITY_LAST, type ParticleEmitter, type ProcessableThing } from "../interfaces";
import { ParticleType } from "../particleType";
import { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import { Chasm } from "./chasm";
import { Floor } from "./floors/floor";
import { LavaPool } from "./lavaPool";
import { WaterTile } from "./waterTile";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";

const DAMAGE = 3;

export class BilePuddle extends PhysicalThing implements ProcessableThing, ParticleEmitter {
	static key = "BilePuddle";
	static readonly LIFETIME_TURNS = 15;
	private turnsRemaining: number = BilePuddle.LIFETIME_TURNS;

	constructor(xy: XY) {
		super(xy, false);
	}

	/** Drop a puddle at `xy`, or restart the countdown of one already there. */
	static placeOrRefresh(board: Board, xy: XY): void {
		if (!BilePuddle.isSolidGround(board, xy)) return;
		const existing = board.getThingOfClass(BilePuddle, xy);
		if (existing !== null) {
			existing.turnsRemaining = BilePuddle.LIFETIME_TURNS;
		} else {
			board.addThing(new BilePuddle(xy));
		}
	}

	// Bile drips through open water/lava/chasm unless a Floor (e.g. a bridge) spans them.
	private static isSolidGround(board: Board, xy: XY): boolean {
		const things = board.getThingsAt(xy);
		const isOpenLiquidOrVoid = things.some(
			(t) => t instanceof WaterTile || t instanceof LavaPool || t instanceof Chasm,
		);
		if (!isOpenLiquidOrVoid) return true;
		return things.some((t) => t instanceof Floor);
	}

	getZOrder(): number {
		return ZOrder.CARPET_LIKE;
	}

	isVisible(): boolean {
		return true;
	}

	isHideUnderneath(): boolean {
		return false;
	}

	processTurn(board: Board): void {
		const player = board.player;
		if (player && player.active && player.xy.isInSquareRadius(this.xy, 1)) {
			player.applyDamage(DamageType.TOXIC_GAS, DAMAGE, this);
		}
		this.turnsRemaining--;
		if (this.turnsRemaining <= 0) {
			board.removeAtEndOfTurn(this);
		}
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	// ParticleEmitter
	getParticleType(): ParticleType {
		return ParticleType.BILE_SMOKE_SMALL;
	}

	// Fades the stink as the puddle dries up toward the end of its life.
	getParticleIntensity(): number {
		return this.turnsRemaining / BilePuddle.LIFETIME_TURNS;
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), turnsRemaining: this.turnsRemaining };
	}

	static deserialize(data: any): BilePuddle {
		const p = new BilePuddle(deserializeXY(data.xy));
		p.visualId = data.visualId;
		p.turnsRemaining = data.turnsRemaining;
		return p;
	}
}
